import { useTranslation } from "react-i18next";

import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { CaretakerResponseDto, UserAccountDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { RootState } from "../app/store";
import { clearErrorCaretakers, deleteCaretaker, fetchCaretakers, selectCaretaker, startDeleteCaretaker, updateSearchTerm } from "../features/caretakersSlice";
import { RouteNames } from "../routes";
import { formatPhone } from "../utils/formatPhone";

import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/TableSkeleton";

export const CaretakersPage = (props: {
    organizationId: string
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const caretakers = useAppSelector((state: RootState) => state.caretakers.caretakers);
    const selectedCaretaker = useAppSelector((state: RootState) => state.caretakers.selectedCaretaker);
    const searchTerm = useAppSelector((state: RootState) => state.caretakers.searchTerm);
    const countries = useAppSelector((state: RootState) => state.country.countries);
    const status = useAppSelector((state: RootState) => state.caretakers.status);
    const error = useAppSelector((state: RootState) => state.caretakers.error);
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchCaretakers({ organizationId: props.organizationId }))
        }

    });

    if (params.caretakerId !== undefined) {
        return <Outlet />
    }

    const onWarnDeleteCaretaker = () => {
        if (selectedCaretaker != null) {
            dispatch(startDeleteCaretaker());
        }
    }

    const onAddCaretaker = () => {
        navigate(`/${RouteNames.organizations}/${props.organizationId}/caretakers/new`);
    }

    const onEditCaretaker = () => {
        if (selectedCaretaker != null) {
            navigate(`/${RouteNames.organizations}/${props.organizationId}/caretakers/${selectedCaretaker.id}`);
        }
    }

    const onReload = () => {

        dispatch(fetchCaretakers({ organizationId: props.organizationId }));
    }

    const onSearch = () => {
        dispatch(fetchCaretakers({ organizationId: props.organizationId, q: searchTerm }));
    }

    const onRow = (caretaker: CaretakerResponseDto, index: number | undefined) => {
        return {
            onClick: () => {
                dispatch(selectCaretaker(caretaker))
            }, // click row
        }
    }

    const onDeleteOrganizationAdmin = () => {
        if (selectedCaretaker != null) {
            dispatch(deleteCaretaker({
                organizationId: props.organizationId,
                caretakerId: selectedCaretaker.id!
            }));
        }
    }

    const getRowClassName = (caretaker: CaretakerResponseDto, index: number) => {
        return selectedCaretaker != null && selectedCaretaker.id === caretaker.id ? 'bg-selected' : '';
    }

    const columns: ColumnsType<CaretakerResponseDto> = [
        {

            // width: '100%',
            title: t("column.firstName"),
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.lastName"),
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.email"),
            dataIndex: 'userAccount',
            key: 'email',
            sorter: (a, b) => a.userAccount?.emailAddress! > b.userAccount?.emailAddress! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => userAccount?.emailAddress
        },
        {

            //width: '100%',
            title: t("column.phone"),
            dataIndex: 'userAccount',
            key: 'phone',
            sorter: (a, b) => a.userAccount?.mobileNumber! > b.userAccount?.mobileNumber! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => formatPhone(userAccount?.mobileNumber, countries)
        },

    ];

    //todo implement server side paging and sorting
    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            <CrudToolbar
                hasSelection={selectedCaretaker != null}
                canEdit={selectedCaretaker?.id != null}
                selectionLabel={[selectedCaretaker?.firstName, selectedCaretaker?.lastName].filter(Boolean).join(" ")}
                onDelete={onWarnDeleteCaretaker}
                onEdit={onEditCaretaker}
                onAdd={onAddCaretaker}
                onReload={onReload}
                onClearSelection={() => dispatch(selectCaretaker(undefined))}
                search={{
                    placeholder: t("placeholder.searchPeople"),
                    value: searchTerm,
                    onChange: value => dispatch(updateSearchTerm(value)),
                    onSearch
                }}
            />
            <div className="card card--flush">
                <Table
                    rowKey={(caretaker: CaretakerResponseDto) => caretaker.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={caretakers}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="caretaker"
                deleteTarget={[selectedCaretaker?.firstName, selectedCaretaker?.lastName].filter(Boolean).join(" ")}
                onDismiss={() => dispatch(clearErrorCaretakers())}
                onConfirmDelete={onDeleteOrganizationAdmin}
            />
        </>

    return content;
}
